import API from './API';

// -----------------------------------------------------------------------------
//  Driver — caricamento a batch e zone strutturate
//
//  `getDrivers()` (in fondo) scarica l'intero elenco: oltre il tetto del
//  backend (DEFAULT_CRUD_LIMIT=1000) i driver sparirebbero senza errore.
//  Le funzioni qui sotto chiedono una pagina per volta e lasciano filtri e
//  ordinamento al database.
//
//  ZONE — il driver ha `provinces` (Regione→Provincia dal seed ISTAT). La M2M
//  `areas` non va più scritta: si legge solo per i driver non ancora migrati.
// -----------------------------------------------------------------------------

const PAGE_SIZE = 25;

const LIST_JOIN = ['agency', 'provinces', 'provinces.region', 'areas'];

// -----------------------------------------------------------------------------
//  ATTENZIONE — `is_alive` sui driver vale il CONTRARIO del suo nome.
//
//  Un driver operativo ha `is_alive = false`: AddDriverPage lo crea con
//  `is_alive: 0` e `registerUser` lo forza a true solo per i *customer*. A DB
//  la colonna ha `DEFAULT '1'`, quindi restano a `true` solo le righe non create
//  dall'applicazione.
//
//  La cancellazione NON passa da qui: il flag vero è `is_deleted`.
// -----------------------------------------------------------------------------
const IS_ALIVE_OPERATIVO = false;

function statusClause(status) {
  if (status === 'attivi') return { is_alive: { $eq: IS_ALIVE_OPERATIVO } };
  if (status === 'inattivi') return { is_alive: { $eq: !IS_ALIVE_OPERATIVO } };
  return null;
}

// I driver cancellati non vanno mai mostrati, in nessun segmento.
const NOT_DELETED = { is_deleted: { $ne: true } };

function normalize(response, fallbackPage = 1) {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { rows: body, total: body.length, page: fallbackPage, pageCount: 1 };
  }
  return {
    rows: body?.data ?? [],
    total: body?.total ?? 0,
    page: body?.page ?? fallbackPage,
    pageCount: body?.pageCount ?? 1,
  };
}

function searchClause(term) {
  const t = (term || '').trim();
  if (!t) return null;
  return {
    $or: [
      { name: { $contL: t } },
      { surname: { $contL: t } },
      { mail: { $contL: t } },
      { telephone_number: { $contL: t } },
    ],
  };
}

export default {
  // Unico punto in cui si legge lo stato di un driver: vedi la nota su
  // `is_alive` qui sopra prima di usare il campo grezzo in una pagina.
  isOperativo(driver) {
    return driver?.is_alive === IS_ALIVE_OPERATIVO;
  },

  // ------------------------------------------------------------- LISTA ---
  // `status`: 'attivi' | 'inattivi' | '' (tutti). Ritorna { rows, total, ... }.
  getDriversPaged({
    page = 1,
    limit = PAGE_SIZE,
    search = '',
    agencyId = '',
    provinceId = '',
    status = 'attivi',
    sort = '',
  } = {}) {
    const and = [NOT_DELETED];
    const st = statusClause(status);
    if (st) and.push(st);
    if (agencyId) and.push({ 'agency.id': { $eq: agencyId } });
    // Le province del driver sono una M2M già in join: il filtro è la stessa
    // condizione che usa `countDriversInProvince`, così elenco e conteggi
    // della capacità parlano degli stessi driver.
    if (provinceId) and.push({ 'provinces.id': { $eq: provinceId } });
    const s = searchClause(search);
    if (s) and.push(s);

    const params = {
      page,
      limit,
      sort: sort || 'surname,ASC',
      join: LIST_JOIN,
    };
    if (and.length) params.s = JSON.stringify({ $and: and });

    return API.get('/driver', { params }).then((r) => normalize(r, page));
  },

  // Driver OPERATIVI che coprono una PROVINCIA.
  //
  // È il numero che decide se una sede ha orari disponibili: il planner cerca
  // i driver per la provincia della sede (planner.service.ts →
  // `driversForZone`, ZONE_MATCHING=province) e da lì scarta chi è già
  // occupato. Provincia senza driver = nessuno slot, a qualsiasi ora.
  //
  // ⚠️ NON si conta più per `areas`: le vecchie zone sono in dismissione e una
  // sede nuova non ne ha (la scheda sede chiede regione e provincia).
  //
  // Non ha nulla a che vedere con `partner.num_driver`, colonna deprecata che
  // non limita più niente (era il tetto di spostamenti interni al giorno).
  countDriversInProvince(provinceId) {
    if (!provinceId) return Promise.resolve(0);
    const s = JSON.stringify({
      $and: [NOT_DELETED, statusClause('attivi'), { 'provinces.id': { $eq: provinceId } }],
    });
    return API.get('/driver', {
      params: { page: 1, limit: 1, join: ['provinces'], s },
    }).then((r) => normalize(r).total);
  },

  countDrivers(status) {
    const and = [NOT_DELETED];
    const st = statusClause(status);
    if (st) and.push(st);
    const params = { page: 1, limit: 1, s: JSON.stringify({ $and: and }) };
    return API.get('/driver', { params }).then((r) => normalize(r).total);
  },

  // --------------------------------------------------------- DETTAGLIO ---
  getDriverDetail(id) {
    return API.get(`/driver/${id}`, { params: { join: LIST_JOIN } });
  },

  // ------------------------------------------------------------- ZONE ---
  // Albero completo Regione→Provincia per le tendine (dato di sistema, sola
  // lettura: le rotte di scrittura sul seed non esistono proprio).
  getRegionsTree() {
    return API.get('/region', {
      params: { join: ['provinces'], sort: 'name,ASC', limit: 50 },
    }).then((r) => normalize(r).rows);
  },

  // SOSTITUISCE l'insieme delle province del driver: si manda lo stato
  // finale, non le differenze (idempotente). Un id inesistente -> 400.
  setDriverProvinces(driverId, provinceIds) {
    return API.patch(`/driver/${driverId}/provinces`, { provinceIds });
  },

  // ------------------------------------------------------- ELENCO COMPLETO ---
  // Scarica TUTTI i driver, senza paginazione.
  getDrivers() {
        return API.get(`/driver?sort=surname,ASC`,{
            params: {
                 join: [
                  'agency',
                  'areas'
                ], 
              },
            });
      }, 

      updateDriver(driver) {
        return API.patch('/driver/' + driver.id, driver)
      },

      getPlannersByBookingId(bookingId) {
        return API.get(`/driver/planner/${bookingId}`)
      },

      // Driver OPERATIVI di una provincia: è la stessa regola del planner
      // (`driversForZone` con ZONE_MATCHING=province), cioè i driver che
      // coprono la provincia della sede.
      //
      // ⚠️ `is_alive: false` significa OPERATIVO: è il nome storico della
      // colonna, rovesciato rispetto a quello che sembra.
      getDriversByProvince(provinceId) {
        if (!provinceId) return Promise.resolve({ data: [] });
        const s = JSON.stringify({
          $and: [
            { 'provinces.id': { $eq: provinceId } },
            { is_alive: { $eq: false } },
            { is_deleted: { $eq: false } },
          ],
        });
        return API.get('/driver', { params: { join: ['provinces'], s } });
      },

      // Vecchia zona (`area`): resta per le sedi che non hanno una provincia.
      getDriverByHeadquarterAreaId(areaId) {
        return API.get(`/driver?filter=areas.id||$eq||${areaId}&filter=is_alive||$eq||${false}&filter=is_deleted||$eq||${false}&`,{
            params: {
                 join: [
                  'areas'
                ], 
              },
            });
      }, 

      // Cambia il driver/jolly di un planner ESISTENTE (serve il plannerId).
      //
      // ⚠️ Non esiste un modo legittimo di creare planner da qui: nascono una
      // volta sola, alla creazione della prenotazione, da
      // POST /planner/best-driver, che ne calcola anche gli orari. Crearne
      // altri con POST /planner li duplicherebbe sulla stessa tratta, con
      // orari inventati a mano.
      postDriverAssign(driverAssign) {
        return API.post('/driver/assign', driverAssign)
      },

      createDriver(driver) {
        return API.post('/auth/register', driver)
      },

      deleteDriver(id) {
        return API.get('/driver/delete/' + id);
      },

}